import NiceModal, { useModal } from '@ebay/nice-modal-react'
import type { MakeLinkOptions } from '@tanstack/router'
import { useNavigate, useSearch } from '@tanstack/router'
import Autoplay from 'embla-carousel-autoplay'
import {
  type ChangeEvent,
  type FormEvent,
  useEffect,
  useMemo,
  useState,
} from 'react'
import TagManager from 'react-gtm-module'
import { useTranslation } from 'react-i18next'

import { COUNTRY } from '../../constants/countries'
import { useEmblaCarouselWithIndex } from '../../hooks/useEmblaCarouselWithIndex'
import { useBoundStore } from '../../store/store'
import type { ApiResponse } from '../../types/api'
import type { UserInfo } from '../../types/user'
import { api } from '../../utils/api-client'
import closeModal from '../../utils/closeModal'
import { getDeviceInfo } from '../../utils/device'
import { getPublicAuth } from '../../utils/getPublicAuth'
import { removeAllSpaces } from '../../utils/removeSpaces'
import sendEventToNativeApp from '../../utils/send-event-to-native-app'
import {
  clearRememberedCredentials,
  encryptUser,
  getRememberedCredentials,
  rememberCredentials,
} from '../../utils/user'
import { Spinner } from '../common'
import { TextInput } from '../register'
import Checkbox from './checkbox'
import DuplicateAccountModal from './duplicate-account-modal'
import HrefLang from './hreflang'
import Modal from './modal'
import RequestPasswordResetModal from './request-password-reset-modal'

const BannerImage = ({ banner }: { banner: string }) => (
  <img
    className="w-full flex mb-1 rounded-[10px] aspect-[3_/_1]"
    src={banner}
    alt="banner"
  />
)

// const BannerLink = ({ url, label }: { url: string; label: string }) => (
//   <a
//     className="text-secondary-txt italic underline text-[10px] xs:text-xs text-center w-full block"
//     href={url}
//     target="blank"
//     aria-label={`Read more about ${label}`}>
//     {label}
//   </a>
// )

const LoginModal = () => {
  const { t } = useTranslation([
    'login',
    'walletManagement',
    'common',
    'homeV2',
  ])
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
  const { redirectUrl } = useSearch() as { redirectUrl?: string }
  const setUserStore = useBoundStore(state => state.user.setUser)
  const b1 = useBoundStore(state => state.settings.login_banner1)
  const b2 = useBoundStore(state => state.settings.login_banner2)
  const b3 = useBoundStore(state => state.settings.login_banner3)
  const b4 = useBoundStore(state => state.settings.login_banner4)
  const b5 = useBoundStore(state => state.settings.login_banner5)
  const b6 = useBoundStore(state => state.settings.login_banner6)

  const modal = useModal()
  const requestPasswordResetModal = useModal(RequestPasswordResetModal)
  const duplicateModal = useModal(DuplicateAccountModal)
  const navigate = useNavigate()
  const remembered = useMemo(() => getRememberedCredentials(), [])
  const [user, setUser] = useState(() => ({
    emailOrPhone: remembered?.username ?? '',
    password: remembered?.password ?? '',
  }))
  const [rememberMe, setRememberMe] = useState(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState('')

  const banners = useMemo(() => {
    return [b1, b2, b3, b4, b5, b6].filter(banner => !!banner.value)
  }, [b1, b2, b3, b4, b5, b6])

  const { emblaRef } = useEmblaCarouselWithIndex({ loop: true }, [
    Autoplay({ stopOnInteraction: false, stopOnMouseEnter: false }),
  ])

  useEffect(() => {
    if (modal.visible) {
      TagManager.dataLayer({
        dataLayer: {
          event: 'login_form_open',
          event_category: 'login',
          event_action: 'click_open',
          event_label: 'Open Login Form',
        },
      })
      sendEventToNativeApp('login_form_open')
    }
  }, [modal.visible])

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    setUser(prev => ({
      ...prev,
      [e.target.name]: ['emailOrPhone', 'password'].includes(e.target.name)
        ? removeAllSpaces(e.target.value)
        : e.target.value,
    }))
  }

  const onSuccess = (info: UserInfo, status: number) => {
    // Register
    if (status === 2) {
      api.post('/conduct.php', {
        type: 'activate_game',
      })
      api.post('/conduct.php', {
        type: 'send_register_email',
      })

      TagManager.dataLayer({
        dataLayer: {
          event: 'registration_success',
          event_category: 'register',
          event_action: 'action_complete',
          event_label: 'Registration Third Step',
        },
      })

      fbq?.('track', 'CompleteRegistration', {
        username: info.username,
        value: 1,
        currency: COUNTRY.currency,
      })

      sendEventToNativeApp('registration_success', {
        email: info.email,
        referral_code: info.referral_code,
      })

      navigate({ to: '/' })
      closeModal(modal)
      return
    }
    // Login
    if (
      location.pathname.includes('/sorteos') ||
      location.pathname.includes('/sports')
    ) {
      location.reload()
    } else {
      if (redirectUrl) {
        navigate({ to: redirectUrl, search: {} } as MakeLinkOptions<'/'>)
        closeModal(modal)
        return
      }

      navigate({ to: '/' })
    }
    TagManager.dataLayer({
      dataLayer: {
        event: 'user_login',
        event_category: 'login',
        event_action: 'action_complete',
        event_label: 'Login Success',
        user_id: info.username,
      },
    })
    sendEventToNativeApp('user_login', info)
    closeModal(modal)

    // Disable casino ads view
    if (
      localStorage.getItem('casino_ads') &&
      +(localStorage.getItem('casino_ads') ?? '0')
    )
      localStorage.setItem('casino_ads', '0')
  }

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (!user.emailOrPhone || !user.password) return

    setError('')
    setIsLoading(true)
    setUserStore({ isLoading: true })

    const publicAuth = await getPublicAuth()

    const reqBody = {
      username: user.emailOrPhone.toLowerCase().trim(),
      password: user.password,
      submit_type: 'login',
      meta: getDeviceInfo(),
      auth: publicAuth,
    }

    const response = await api.post<ApiResponse<UserInfo>>(
      '/center.php',
      reqBody,
    )

    const { status, info } = response.data

    if (status === 1) {
      const privateAuth = await api.post<
        ApiResponse<string | { auth: string; expired_time: number }>
      >('/link.php', {
        username: info.username,
        password: user.password,
        auth: publicAuth,
      })
      if (
        privateAuth.data.status === 1 &&
        typeof privateAuth.data.info !== 'string'
      ) {
        localStorage.setItem('privateAuth', privateAuth.data.info.auth)
      } else {
        if (typeof privateAuth.data.info === 'string') {
          setError(privateAuth.data.info)
        }
        setIsLoading(false)
        return
      }
      encryptUser({ username: info.username, password: user.password })
      if (rememberMe) {
        rememberCredentials({
          username: info.username,
          password: user.password,
        })
      } else {
        clearRememberedCredentials()
      }
      setUserStore({ ...info, isLoading: false })
      onSuccess(info, 1)
    } else if (status === 2) {
      if (typeof info === 'string') {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore
        navigate({ to: info })
        closeModal(modal)
      }
    } else if (status === 3) {
      duplicateModal.show()
      closeModal(modal)
    } else {
      if (typeof info === 'string') setError(info as unknown as string)
    }
    setIsLoading(false)
  }

  return (
    <>
      <HrefLang page="login" />
      <Modal
        id="log-in-modal"
        containerStyle="!bg-secondary !w-[calc(100%_-_60px)] xs:!w-[calc(100%_-_32px)] !max-h-[calc(100vh_-_32px)] !overflow-y-auto !overflow-x-hidden !pt-5 !pb-[15px] !px-3 xs:!px-5 !rounded-[10px] !border-0">
        <div className="flex bg-primary justify-end -mt-5 mb-3 xs:mb-5 -mx-5 px-5 py-0 rounded-[20px_20px_0_0]">
          <button
            className="text-xs xs:text-sm text-highlight px-0 py-2.5"
            onClick={() => {
              navigate({ to: '/register' })
              closeModal(modal)
            }}>
            {t('common:register').toUpperCase()}
          </button>
        </div>
        {!!banners.length && (
          <div className="relative">
            {banners.length === 1 ? (
              <div className="mt-2.5 mb-3">
                <BannerImage banner={banners[0].value as string} />
              </div>
            ) : (
              <div
                className="embla w-full max-w-[500px] mx-auto mt-2.5 mb-3 xl:mx-auto"
                ref={emblaRef}>
                <div className="embla__container">
                  {banners.map((banner, index) => (
                    <div key={index} className="relative embla__slide">
                      {banner.value && <BannerImage banner={banner.value} />}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        <form onSubmit={onSubmit}>
          {error && (
            <p className="text-xs xs:text-sm font-normal text-[rgb(255,64,88)] w-full mb-2.5 px-0 py-0.5 xl:text-base">
              {error}
            </p>
          )}
          <TextInput
            label="E-mail / Número de teléfono"
            value={user.emailOrPhone}
            name="emailOrPhone"
            onChange={onChange}
            size="small"
          />
          <TextInput
            label={t('password', { ns: 'login' })}
            isPassword
            value={user.password}
            name="password"
            onChange={onChange}
            size="small"
          />
          <label className="flex items-center gap-2 mt-3 text-xs xs:text-sm cursor-pointer select-none text-secondary-txt">
            <Checkbox
              icon="check"
              checked={rememberMe}
              onChange={e => setRememberMe(e.target.checked)}
              iconColor="text-secondary-txt dark:text-white"
              className="!rounded-sm !ring-1 !ring-secondary-txt dark:!ring-white !w-[12px] !h-[12px]"
            />
            <span>{t('remember-me', { ns: 'login' })}</span>
          </label>
          <div className="flx-ctr mt-6">
            {isLoading ? (
              <Spinner spinnerSize={39} />
            ) : (
              <button
                className="block text-center bg-highlight text-[white] transition-all duration-[0.2s] ease-[ease-in-out] cursor-pointer uppercase text-xs xs:text-sm w-full px-6 py-2 xs:py-3 rounded-md disabled:bg-[#657f8c] xl:text-base"
                style={{ margin: '0 auto' }}
                type="submit">
                {t('login')}
              </button>
            )}
          </div>
        </form>
        <p
          className="text-highlight cursor-pointer underline text-xs xs:text-sm text-center mt-3 xs:mt-4 xs:mb-[5px]"
          onClick={() => {
            requestPasswordResetModal.show()
            closeModal(modal)
          }}>
          {t('forgot-password')}
        </p>
      </Modal>
    </>
  )
}

export default NiceModal.create(LoginModal)
